import { useEffect, useRef, useState } from 'react';
import { Loader, Table, Group } from '@mantine/core';
import Header from '../../components/util/header';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import PaginationComponent from '../../components/pagination';
import { ICurrency } from '../../../libraries/currencies';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import { fetchWithdrawSets, IWithdrawSets, IWithdrawSetsResponse } from '../../../libraries/brisa';
import { calculateTotal, formatCurrAmount } from '../../utils/script';
import AddEditWithdrawSet from './edit-withdraw-set';
import './styles.scss';

const WithdrawSetList = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const currencies = useSelector<AppState, ICurrency[]>(state => state.currencies.currencies);
  const controller = new AbortController();

  const [loading, setLoading] = useState(false);
  const [totalRowsResponse /* setTotalRowsResponse */] = useState<number | undefined>();
  const [offset, setOffset] = useState<number>(0);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 0,
    minHeightForDynamic: 400
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [withdrawSetList, setWithdrawSetList] = useState<IWithdrawSetsResponse>();
  const [noResults, setNoResults] = useState<boolean>(true);
  const [statusOperCode, setStatusOperCode] = useState<number>();
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();

  const [selectedWithdrawSet, setSelectedWithdrawSet] = useState<IWithdrawSets | null>(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [modalMode, setModalMode] = useState<'new' | 'edit'>('edit');

  useEffect(() => {
    if (filterBarData && limit) {
      loadTransactionList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadTransactionList = () => {
    if (loading) {
      controller.abort();
    }
    if (!filterBarData || !limit) {
      return;
    }
    setLoading(true);
    setWithdrawSetList(undefined);

    fetchWithdrawSets(filterBarData.entity || 0)
      .then(response => {
        if (response.statusOper.code === 0) {
          setWithdrawSetList(response);
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
        } else {
          setNoResults(true);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  //----------------------------------------------------------------//

  const handleRowClick = (withdrawSet: IWithdrawSets) => {
    setSelectedWithdrawSet(withdrawSet);
    setModalMode('edit');
    setModalOpened(true);
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const handleNewWithdrawSet = () => {
    setSelectedWithdrawSet(null);
    setModalMode('new');
    setModalOpened(true);
  };

  return (
    <div className="withdraw-sets">
      {modalOpened && (
        <AddEditWithdrawSet
          withdrawSet={selectedWithdrawSet || undefined}
          mode={modalMode}
          opened={modalOpened}
          withdrawSetList={withdrawSetList}
          defaultEntid={filterBarData?.entity}
          close={() => {
            setModalOpened(false);
            setSelectedWithdrawSet(null);
          }}
          onSaved={() => {
            setModalOpened(false);
            setSelectedWithdrawSet(null);
            loadTransactionList();
          }}
        />
      )}

      <Header pageTitle={lang.BRISA_WITHDRAW_SETS_TITLE} />

      <FilterBar
        onDataChange={handleFilterBarOnDataChange}
        onSearch={loadTransactionList}
        fieldsUsage={{
          entity: true
        }}
        addButton={handleNewWithdrawSet}
      />

      <div ref={tableWrapperRef}>
        <Table.ScrollContainer minWidth={500} ref={tableRef}>
          <Table striped verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{lang.BRISA_WITHDRAW_SETS_ENTITY}</Table.Th>
                <Table.Th>{lang.BRISA_WITHDRAW_SETS_ID}</Table.Th>
                <Table.Th>{lang.BRISA_WITHDRAW_SETS_DESCRIPTION}</Table.Th>
                <Table.Th>{lang.BRISA_WITHDRAW_SETS_AMOUNT}</Table.Th>
              </Table.Tr>
            </Table.Thead>

            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={16}>
                    <Loader color="#016273" />
                  </Table.Td>
                </Table.Tr>
              ) : (
                <>
                  {noResults ? (
                    <Table.Tr>
                      <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                        {lang.GLOBAL_NO_RESULTS}
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    withdrawSetList?.list.map((withdrawSet, index) => {
                      const matchingCurrency = currencies?.[0];
                      return (
                        <Table.Tr key={index} style={{ cursor: 'pointer' }} onClick={() => handleRowClick(withdrawSet)}>
                          <Table.Td>{withdrawSet.entid}</Table.Td>
                          <Table.Td>{withdrawSet.id}</Table.Td>
                          <Table.Td>{withdrawSet.description}</Table.Td>
                          <Table.Td>
                            {formatCurrAmount(calculateTotal(withdrawSet.mc, matchingCurrency), matchingCurrency)}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })
                  )}
                </>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </div>

      <Group>
        <PaginationComponent
          total={totalRowsResponse}
          statusOperCode={statusOperCode}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>
    </div>
  );
};

export default WithdrawSetList;
